// config.js - Complete Robust Implementation

// Health check state management
let localServerChecked = false;
let localServerAvailable = false;

/**
* Robust health check for local Python server with retries
*/
const checkLocalPythonServer = async () => {
  if (localServerChecked) {
    console.debug(`Using cached local server status: ${localServerAvailable}`);
    return localServerAvailable;
  }

  const maxRetries = 3;
  const retryDelay = 300;
  const timeoutDuration = 800;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.debug(`Checking local server (attempt ${attempt})...`);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

      const response = await fetch(
        "http://127.0.0.1:8000/api/health",
        {
          method: "HEAD",
          cache: "no-store",
          signal: controller.signal,
          credentials: "omit", // No need for credentials in health check
        },
      );

      clearTimeout(timeoutId);

      if (response.ok) {
        console.debug('Local Python server detected');
        localServerChecked = true;
        localServerAvailable = true;
        return true;
      }
    } catch (error) {
      console.debug(`Health check attempt ${attempt} failed:`, error.message);
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }

  console.debug('Local Python server not detected after all attempts');
  localServerChecked = true;
  localServerAvailable = false;
  return false;
};

/**
* Determines the appropriate backend URL based on environment
*/
export const getBackendUrl = async () => {
  const fallbackUrl =
    process.env.REACT_APP_BACKEND_API_URL ||
    "https://samarth-qa-backend.azurewebsites.net";

  // Avoid local server health check during Jest tests
  if (process.env.NODE_ENV === "test") {
    return fallbackUrl;
  }

  const hostname =
    typeof window !== "undefined" && window.location
      ? window.location.hostname
      : "";

  const isLocalhost =
    hostname === "localhost" || hostname === "127.0.0.1";

  // Always use backend URL outside local browser environment
  if (!isLocalhost) {
    return fallbackUrl;
  }

  const isPythonRunning = await checkLocalPythonServer();

  return isPythonRunning ? "http://127.0.0.1:8000" : fallbackUrl;
};


// Base API configuration
const apiConfig = {
  // Default fetch options for all API calls
  fetchOptions: {
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  },

  // Dynamic base URL with getter/setter
  get baseUrl() {
    return this._baseUrl || "https://samarth-qa-backend.azurewebsites.net";
  },
  set baseUrl(url) {
    // console.debug(`Setting base URL to: ${url}`);
    this._baseUrl = url;

    // Adjust fetch options for local development
    if (url.includes("localhost") || url.includes("127.0.0.1")) {
      // console.debug('Configuring for local development');
      this.fetchOptions.mode = "no-cors";
    } else {
      // console.debug('Configuring for production environment');
      this.fetchOptions.mode = "cors";
    }
  },

  // API endpoints
  get getMasterApi() {
    return `${this.baseUrl}/api/getFilterOptions`;
  },
  get getInterlinkageRecords() {
    return `${this.baseUrl}/api/getFilteredDropdowns`;
  },
  get getOptimizedFilterDetails() {
    return `${this.baseUrl}/api/getSupplierFilterOptions`;
  },
  get getRoleBaseFilterDetails() {
    return `${this.baseUrl}/api/get_user_access`;
  },
  get adminApi() {
    return `${this.baseUrl}/api/get-user-details`;
  },
  get landingApi() {
    return `${this.baseUrl}/api/landing`;
  },
  get UserApi() {
    return `${this.baseUrl}/api/user_details`;
  },
  get CreateUserApi() {
    return `${this.baseUrl}/api/add-user`;
  },
  get DeleteUserApi() {
    return `${this.baseUrl}/api/delete-user`;
  },
  get DeleteAllUserApi() {
    return `${this.baseUrl}/api/delete-users`;
  },
  get EditUserApi() {
    return `${this.baseUrl}/api/edit-user`;
  },
  get PersonaApi() {
    return `${this.baseUrl}/api/persona_details`;
  },
  get AddPersonaApi() {
    return `${this.baseUrl}/api/add-persona-access`;
  },
  get EditPersonaApi() {
    return `${this.baseUrl}/api/edit-persona-access`;
  },
  get DeletePersonaApi() {
    return `${this.baseUrl}/api/delete-persona-access`;
  },
  get ExportUserApi() {
    return `${this.baseUrl}/api/export_user_details`;
  },
  get alertDetailApi() {
    return `${this.baseUrl}/api/RMPMShortage`;
  },
  get demandDetailApi() {
    return `${this.baseUrl}/api/DemandSupply`;
  },
  get keyInsight() {
    return `${this.baseUrl}/api/keyInsights`;
  },
  get executeAction() {
    return `${this.baseUrl}/api/executeAction`;
  },
  get mmInputScreen() {
    return `${this.baseUrl}/api/get_mm_screen_input`;
  },
  get mmInputScreenTbl() {
    return `${this.baseUrl}/api/get_mm_screen_input_tbl`;
  },
  get tableViewDailyPhasing() {
    return `${this.baseUrl}/api/get_dp_table_view`;
  },
  get scheduleViewDailyPhasing() {
    return `${this.baseUrl}/api/get_dp_schedule_view`;
  },
  get submit_mm_input() {
    return `${this.baseUrl}/api/submit_mm_input_data`;
  },
  get save_draft_mm_input() {
    return `${this.baseUrl}/api/save_draft_mm_input_data`;
  },
  get get_cbu_details() {
    return `${this.baseUrl}/api/get_cbu_details`;
  },
  get get_cascade_line() {
    return `${this.baseUrl}/api/get_cascade_line_from_capacity_master`;
  },
  get export() {
    return `${this.baseUrl}/api/export_to_excel`;
  },
  get exportExcel() {
    return `${this.baseUrl}/api/export_excel`;
  },
  get exportSupplierExcel() {
    return `${this.baseUrl}/api/export-supplier-insights`;
  },
  get normAlert() {
    return `${this.baseUrl}/api/norm_alert`;
  },
  get ContractAlert() {
    return `${this.baseUrl}/api/ContractAlert`;
  },
  get ContractPR2Alert() {
    return `${this.baseUrl}/api/ContractPR2Alert`;
  },
  get aglieOR() {
    return `${this.baseUrl}/api/AglieOR`;
  },
  get agileDemandDetailsApi() {
    return `${this.baseUrl}/api/DemandSupplyAgile`;
  },
  get agileKeyInsightApi() {
    return `${this.baseUrl}/api/keyInsightsAgile`;
  },
  get weeklyOR() {
    return `${this.baseUrl}/api/WeeklyOR`;
  },
  get saveFilterPreferences() {
    return `${this.baseUrl}/api/saveFilterPreferences`;
  },
  get saveColumnPreferences() {
    return `${this.baseUrl}/api/saveColumnPreferences`;
  },
  get customizeColumns() {
    return `${this.baseUrl}/api/customize-columns`;
  },
  get agingAPI() {
    return `${this.baseUrl}/api/executeAging`;
  },
  get homeLanding() {
    return `${this.baseUrl}/api/homelanding`;
  },
  get homeLandingcbu() {
    return `${this.baseUrl}/api/homelandingcbu`;
  },
  get homeLandingrmpm() {
    return `${this.baseUrl}/api/homelandingrmpm`;
  },
  get supplierInsightsAPI() {
    return `${this.baseUrl}/api/supplierInsights`;
  },
  get vendorOtifAPI() {
    return `${this.baseUrl}/api/vendorOtif`;
  },
  get vendorQCAPI() {
    return `${this.baseUrl}/api/qualityConformanceIndex`;
  },
  get overduePOAPI() {
    return `${this.baseUrl}/api/overduePO`;
  },
  get leadTimeAnalysisAPI() {
    return `${this.baseUrl}/api/leadTimeAnalysis`;
  },
  get sobComplianceAPI() {
    return `${this.baseUrl}/api/sobCompliance`;
  },
  get chatBotAsk() {
    return `${this.baseUrl}/api/excutechat`;
  },
  get chatBotStartConversation() {
    return `${this.baseUrl}/api/start-conversation`;
  },
  get chatBotFollowUp() {
    return `${this.baseUrl}/api/follow-up`;
  },
  get chatBotFeedback() {
    return `${this.baseUrl}/api/feedback`;
  },
  get fgCoverageAPI() {
    return `${this.baseUrl}/api/fgCoverage`;
  },
  get manpowerShortage() {
    return `${this.baseUrl}/api/manpower_shortage`;
  },
  get maiAPI() {
    return `${this.baseUrl}/api/MAIDetails`;
  },
  get openPOAPI() {
    return `${this.baseUrl}/api/OPENPODetails`;
  },
  get manpowerShortageGraph() {
    return `${this.baseUrl}/api/manpower_shortage_graph`;
  },
  get manpowerShortageParameter() {
    return `${this.baseUrl}/api/manpower_shortage_parameter`;
  },
  get otherRecommendationFeedback() {
    return `${this.baseUrl}/api/otherRecommendation`;
  },
  get buyerComment() {
    return `${this.baseUrl}/api/buyerComment`;
  },
  get factoryComment() {
    return `${this.baseUrl}/api/factoryComment`;
  },
  get prioritizationMatrix() {
    return `${this.baseUrl}/api/FetchMatrix`;
  },
  get updPrioritizationMatrix() {
    return `${this.baseUrl}/api/UpdateMatrix`;
  },
  get addPrioritizationMatrix() {
    return `${this.baseUrl}/api/insert_matrix_data`;
  },
  get getCbuWeekPlan() {
    return `${this.baseUrl}/api/get_cbu_week_plan`;
  },
  get exportExcelPrioritization() {
    return `${this.baseUrl}/api/ExportMatrixExcel`;
  },
  get cbu_line_level_response() {
    return `${this.baseUrl}/api/get_line_level_plan`;
  },
  get cbu_level_response() {
    return `${this.baseUrl}/api/get_cbu_level_plan`;
  },
  get delete_cbu_line_level_response() {
    return `${this.baseUrl}/api/delete_and_audit_mm_screen_input`;
  },
  get aggregate_plan_actual() {
    return `${this.baseUrl}/api/aggregate_plan_actual`;
  },
  get cbu_line_level_export() {
    return `${this.baseUrl}/api/export_dps_line_cbu_level_table`;
  },
  get exportExcelAgileORInsight() {
    return `${this.baseUrl}/api/agile_or_insight_excel_export`;
  },
  get validateNewCBUFromExcel() {
    return `${this.baseUrl}/api/validate_imported_excel_data`;
  },
  get productionPlanComparison() {
    return `${this.baseUrl}/api/ProdPlanComparison`;
  },
  get systemVsMMPlanData() {
    return `${this.baseUrl}/api/system_mm_plan`;
  },
  get adoptionPlanData() {
    return `${this.baseUrl}/api/AdoptionInsight`;
  },
  get planChangeReasonTrend() {
    return `${this.baseUrl}/api/PlanChangeReasonTrend`;
  },
  get priorityGraphInsight() {
    return `${this.baseUrl}/api/PriorityGraphInsight`;
  },
  get trackActivityApi() {
    return `${this.baseUrl}/api/track_activity`;
  },
  get actionSummaryApi() {
    return `${this.baseUrl}/api/action_summary`;
  },
  get fetchAeraPlanData() {
    return `${this.baseUrl}/api/thursday_aera_popup`;
  },
  get fetchOverloadedData() {
    return `${this.baseUrl}/api/calculate_shift_status`;
  },
  get lastRefreshTimeDetails() {
    return `${this.baseUrl}/api/lastRefreshTime`;
  },
  get underLyingDataExportApi() {
    return `${this.baseUrl}/api/underlying_export_data`;
  },
  get getTonsPerShift() {
    return `${this.baseUrl}/api/get_tons_per_shift`;
  },
  get handleTrackingUpdate() {
    return `${this.baseUrl}/api/dailyphasing_input_tracking`;
  },
  get changeOverTimeDetails() {
    return `${this.baseUrl}/api/changeover_time_visibility`;
  },
  get getPODetails() {
    return `${this.baseUrl}/api/get_po_details_mai_page`;
  },
  // ORLT --start

  get orLineFill() {
    return `${this.baseUrl}/api/orLineFill`;
  },
  // suggested order api endpoints
  get fetchSuggestedHighRiskSKUData() {
    return `${this.baseUrl}/api/high_risk_skus_table`;
  },
  get fetchSuggestedKPIs() {
    return `${this.baseUrl}/api/high_risk_skus_kpis`;
  },
  get fetchSuggestedDCwiseSuggested() {
    return `${this.baseUrl}/api/dc_wise_suggested_orders_graph`;
  },
  get fetchSuggestedOrderFilters() {
    return `${this.baseUrl}/api/suggested_order_filters`;
  },
  get fetchDispatchPriorityCustomerBucketCount() {
    return `${this.baseUrl}/api/customer_bucket_count`;
  },
  get fetchSuggestedOrderOverviewTable() {
    return `${this.baseUrl}/api/suggested_order_overview_table`;
  },
  get exportSuggestedHighRiskSkus() {
    return `${this.baseUrl}/api/export_excel_high_risk_skus`;
  },
  get exportSuggestedOrderOverview() {
    return `${this.baseUrl}/api/export_excel_suggested_order_overview`;
  },
  get parentSuggestedOrderExport() {
    return `${this.baseUrl}/api/export_all_suggested_order`;
  },
  // dispatch priority api endpoints
  get fetchDispatchPriorityFilters() {
    return `${this.baseUrl}/api/dispatch_prio_filters`;
  },
  //Samarth Api endpoints
  get chatBotStartConversationV2() {
    return `${this.baseUrl}/api/start_conversation_v2`;
  },
  get chatBotFollowUpV2() {
    return `${this.baseUrl}/api/follow_up_v2`;
  },
  get chatBotFeedbackV2() {
    return `${this.baseUrl}/api/feedback`;
  },
  get samarthaiGetConvservationListV2() {
    return `${this.baseUrl}/api/samarthai_conversations`;
  },
  get samarthaiRenameConversationTitleV2() {
    return `${this.baseUrl}/api/samarthai_rename_conversation`;
  },
  get samarthaiFaqV2() {
    return `${this.baseUrl}/api/samarthai_conversations`;
  },
  get samarthaiGetConvservationIdV2() {
    return `${this.baseUrl}/api/samarthai_conversations`;
  },
  get samarthaiCreateDashboard() {
    return `${this.baseUrl}/api/samarthai/dashboard/create`;
  },
  get samarthaiDeleteDashboard() {
    return `${this.baseUrl}/api/samarthai/dashboard/delete`;
  },
  get samarthaiRenameDashboard() {
    return `${this.baseUrl}/api/samarthai/dashboard/rename`;
  },
  get samarthaiPinned() {
    return `${this.baseUrl}/api/samarthai/pin_unpin_prompt`;
  },
  get samarthaiGetPinnedPrompts() {
    return `${this.baseUrl}/api/samarthai_conversations`;
  },
  get samarthaiBookmarked() {
    return `${this.baseUrl}/api/samarthai/bookmark_conversation`;
  },
  get samarthaiGetBookmarkedPrompts() {
    return `${this.baseUrl}/api/samarthai_conversations`;
  },
  get samarthaiGraph() {
    return `${this.baseUrl}/api/samarthai/generate_graph`;
  },
  get samarthaiDashboardList() {
    return `${this.baseUrl}/api/samarthai/dashboard`;
  },
  get samarthaiAddDashboard() {
    return `${this.baseUrl}/api/samarthai/dashboard/view/create`;
  },
  get samarthaiAddDashboardList() {
    return `${this.baseUrl}/api/samarthai/dashboard/view/list`;
  },
  get samarthaiDeleteDashboardList() {
    return `${this.baseUrl}/api/samarthai/dashboard/view/delete`;
  },
  get samarthaiDashboardOrderPlacement() {
    return `${this.baseUrl}/api/samarthai/dashboard/view/update_order`;
  },
  get samarthaiDeleteConversation() {
    return `${this.baseUrl}/api/samarthai/conversation/delete`;
  },
  get samarthaiDashboardRefresh() {
    return `${this.baseUrl}/api/samarthai/dashboard/view/refresh`;
  },
  get alertSummaryFilters() {
    return `${this.baseUrl}/api/AlertSummaryFilters`;
  },
  get alertSummaryData() {
    return `${this.baseUrl}/api/AlertSummary`;
  },
  get alertSummaryKPIData() {
    return `${this.baseUrl}/api/alert_summary_kpi`;
  },
  get alertSummarySiteAdaptionData() {
    return `${this.baseUrl}/api/site_adaption`;
  },
  get alertSummaryChartData() {
    return `${this.baseUrl}/api/recommendation_causes`
  },
  get alertSummaryTrendsData() {
    return `${this.baseUrl}/api/alert_summary_trends`;
  },
  get orLossExportToExcel() {
    return `${this.baseUrl}/api/or-loss-export-excel`;
  },
  get l3LossGetAPI() {
    return `${this.baseUrl}/api/loss3popup`;
  },
  get l3LossSaveAPI() {
    return `${this.baseUrl}/api/loss3popup`;
  },
  get orHeatMapWeek() {
    return `${this.baseUrl}/api/weeklytrendheatmap`;
  },
  get orHeatMapAllLosses() {
    return `${this.baseUrl}/api/heatmapAllLosses`;
  },

  get orLossFilters() {
    return `${this.baseUrl}/api/OrLossFilters`;
  },
  get orLossKPICards() {
    return `${this.baseUrl}/api/KpiCards`;
  },
  get orLossFactoryLevel() {
    return `${this.baseUrl}/api/FactoryLevel`;
  },
  get orLossBreakdownByBg() {
    return `${this.baseUrl}/api/BreakdownByBg`;
  },
  get orLossWeeklyTrend() {
    return `${this.baseUrl}/api/WeeklyTrend`;
  },
  get orLossBuckets() {
    return `${this.baseUrl}/api/LossBuckets`;
  },
  get orLossWeeklyTrendLossTree() {
    return `${this.baseUrl}/api/WeeklyTrendLossTree`;
  },
  get orLossRmTable() {
    return `${this.baseUrl}/api/rm_table`;
  },
  get weeklyTrendExport() {
    return `${this.baseUrl}/api/weekly-trend-export-excel`;
  },
  get orLossHeatmapExport() {
    return `${this.baseUrl}/api/loss-tree-heatmap-export-excel`;
  },

  // ORLT --end
  get incidentReportApi() {
    return `${this.baseUrl}/api/report_incident`;
  },

  // change management endpoints
  get fetchIncidentsApi() { 
    return `${this.baseUrl}/api/get_incidents`; 
  },
  get updateIncidentPriorityApi() { 
    return `${this.baseUrl}/api/update_priority`; 
  },
  get assignTicketApi() { 
    return `${this.baseUrl}/api/assign_ticket`; 
  },
  get updateTicketStatusApi() { 
    return `${this.baseUrl}/api/update_ticket_status`; 
  },
  get getBauTeamDetails() { 
    return `${this.baseUrl}/api/get_bau_team_details`; 
  },
  get getKpis() { 
    return `${this.baseUrl}/api/get_kpi_details`; 
  },
  get postEffortDetails() { 
    return `${this.baseUrl}/api/save_ticket_efforts`; 
  },
  get exportTicketReport() { 
    return `${this.baseUrl}/api/change_management_report`; 
  },
  get updateHolidaysList() { 
    return `${this.baseUrl}/api/upload_holidays`; 
  },
  get syncBauTeamDetails() { 
    return `${this.baseUrl}/api/sync_bau_team`; 
  },
  get getTicketDetails() { 
    return `${this.baseUrl}/api/get_ticket_details`; 
  },
  get postTicketComment() { 
    return `${this.baseUrl}/api/add_ticket_comment`; 
  },
  get saveTicketRca() { 
    return `${this.baseUrl}/api/save_ticket_rca`; 
  },
  get getInternalTeam() { 
    return `${this.baseUrl}/api/get_internal_team_members`; 
  },
  get updateTicketDetails() {
    return `${this.baseUrl}/api/update_ticket_details`
  },

  // qcom api endpoints
  get fetchHighRiskSKUData() {
    return `${this.baseUrl}/api/high_risk_skus_table`;
  },
  get fetchDispatchKPIs() {
    return `${this.baseUrl}/api/high_risk_skus_kpis`;
  },
  get fetchDispatchDCwiseSuggested() {
    return `${this.baseUrl}/api/dc_wise_suggested_orders_graph`;
  },
  get fetchDispatchSuggestedOrderFilters() {
    return `${this.baseUrl}/api/suggested_order_filters`;
  },
  get fetchDispatchSuggestedOrderOverviewTable() {
    return `${this.baseUrl}/api/suggested_order_overview_table`;
  },
  get fetchDispatchPriorityMinMaxGraph() {
    return `${this.baseUrl}/api/min_max_dispatch_dc_graph`;
  },
  get fetchDispatchPriorityKPI() {
    return `${this.baseUrl}/api/dispatch_priority_kpi_tiles`;
  },
  get fetchDispatchPriorityOverviewTable() {
    return `${this.baseUrl}/api/overview_table_dispatch_prio`;
  },
  get fetchDispatchPriorityGoodenessGraph() {
    return `${this.baseUrl}/api/bg_wise_prioritization_goodness_graph`;
  },
  get fetchDispatchDCwisePriorityGoodenessGraph() {
    return `${this.baseUrl}/api/dc_wise_prioritization_goodness_graph`;
  },
  get exportDispatchPrioOverview() {
    return `${this.baseUrl}/api/export_dispatch_prio`;
  },
  get parentDispatchPrioExport() {
    return `${this.baseUrl}/api/export_dispatch_priority_all`;
  },
  // PO priority api endpoints
  get fetchPOPriorityFilters() {
    return `${this.baseUrl}/api/po_priority_filters`;
  },
  get fetchPOValidityBuckets() {
    return `${this.baseUrl}/api/po_validity_buckets`;
  },
  get fetchPOPriorityOverview() {
    return `${this.baseUrl}/api/po_priority_overview`;
  },
  get fetchCustomerLevelPOWisePriorityStatus() {
    return `${this.baseUrl}/api/customer_level_po_wise_priority_status`;
  },
  get fetchDCLevelPOWisePriorityStatus() {
    return `${this.baseUrl}/api/dc_level_po_wise_priority_status`;
  },
  get exportPOPriorityOverview() {
    return `${this.baseUrl}/api/export_po_priority_overview`;
  },
  get exportPOPriorityWholeScreen() {
    return `${this.baseUrl}/api/export_po_priority_whole_screen`;
  },
  //loss recovery api endpoints
  get fetchLossRecoveryKPIs() {
    return `${this.baseUrl}/api/loss_recovery_kpi`;
  },
  get fetchBGLevelSummary() {
    return `${this.baseUrl}/api/bg_level_summary`;
  },
  get fetchChannelLevelSummary() {
    return `${this.baseUrl}/api/channel_level_summary`;
  },
  get fetchLossRecoveryFilters() {
    return `${this.baseUrl}/api/loss_recovery_filters`;
  },
  get fetchLossRecoveryOverView() {
    return `${this.baseUrl}/api/loss_recovery_overview_summary_table`;
  },
  get exportLossRecoveryOverview() {
    return `${this.baseUrl}/api/export_loss_recovery_engine_overview`;
  },
  //INTR api endpoints
  get INTRRecoveryFilters() {
    return `${this.baseUrl}/api/intr_filters`;
  },
  get INTROverviewTable() {
    return `${this.baseUrl}/api/overview_table_intr_recovery`;
  },
  get INTRExport() {
    return `${this.baseUrl}/api/export_intr_recovery`;
  },
  // Unloading prioritization api endpoints
  get UnloadingPriorityTable() {
    return `${this.baseUrl}/api/overview_table_unloading_tabledata`;
  },

  //order to allocation
  get fetchOrderToAllocationFilters() { return `${this.baseUrl}/api/order_to_execution_filter`; },
  get fetchOrderToAllocationLossReasons() { return `${this.baseUrl}/api/order_to_execution_lossreasons`; },
  get fetchOrderToAllocationLossValidity() { return `${this.baseUrl}/api/order_to_execution_lossvalidityvalue`; },
  get fetchOrderToAllocationLossCategory() { return `${this.baseUrl}/api/order_to_execution_losscategory`; },
  get fetchOrderToAllocationLossHeatMap() { return `${this.baseUrl}/api/order_to_execution_lossheatmap`; },
  get fetchOrderToAllocationOverviewTable() { return `${this.baseUrl}/api/order_to_execution_overview_summary_table`; },
  //allocation to delivery
  get fetchAllocationToDeliveryFilters() { return `${this.baseUrl}/api/order_to_execution_filter`; },
  get fetchAllocationToDeliveryLossReasons() { return `${this.baseUrl}/api/allocation_to_delivery_lossreasons`; },
  get fetchAllocationToDeliveryLossValidity() { return `${this.baseUrl}/api/order_to_execution_lossvalidityvalue`; },
  get fetchAllocationToDeliveryLossCategory() { return `${this.baseUrl}/api/allocation_to_delivery_losscategory`; },
  get fetchAllocationToDeliveryOverviewTable() { return `${this.baseUrl}/api/allocation_to_delivery_overview_summary_table`; },
  //INTR api endpoints
  get INTRMaterialRecoveryFilters() { return `${this.baseUrl}/api/material_view_filters`; },
  get INTRMaterialOverviewTable() { return `${this.baseUrl}/api/material_view_overview_table`; },
  get INTRMaterialExport() { return `${this.baseUrl}/api/export_materialview_tabledata`; },
  get INTRRecoveryGlobalGraph() { return `${this.baseUrl}/api/intr_recovery_graph`; },
  get INTRRecoveryShipmentTable() { return `${this.baseUrl}/api/intr_recovery_shiptable`; },
  get MaterialViewGraph() { return `${this.baseUrl}/api/material_view_graph`; },
  get MaterialViewSummaryTable() { return `${this.baseUrl}/api/material_view_summary_table`; },
  // Unloading prioritization global api endpoints
  get UnloadingPriorityGlobalviewFilters() { return `${this.baseUrl}/api/unloading_priority_globalview_filter`; },
  get UnloadingPriorityGlobalviewOverviewTable() { return `${this.baseUrl}/api/overview_table_unloading_tabledata`; },
  get UnloadingPriorityGlobalviewExport() { return `${this.baseUrl}/api/export_unloading_tabledata`; },
  get UnloadingPriorityGlobalFifoGraph() { return `${this.baseUrl}/api/unloading_prioritization_globalview_fifograph`; },
  get UnloadingPriorityShipmentTable() { return `${this.baseUrl}/api/unloading_prioritization_globalview_visitable`; },
  // Unloading prioritization shipment api endpoints
  get UnloadingPriorityShipmentviewFilters() { return `${this.baseUrl}/api/unloading_priority_shipmentview_filter`; },
  get UnloadingPriorityShipmentviewOverviewTable() { return `${this.baseUrl}/api/unloading_prioritization_shipmentview_overview_table`; },
  get UnloadingPriorityShipmentviewExport() { return `${this.baseUrl}/api/export_unloading_prioritization_shipmentview_tabledata`; },
  // In-Transit Recovery global api endpoints
  get InTransitRecoveryGlobalviewFilters() { return `${this.baseUrl}/api/in_transit_recovery_globalview_filter`; },
  get InTransitRecoveryGlobalviewOverviewTable() { return `${this.baseUrl}/api/in_transit_globalview_overviewtable`; },
  get InTransitRecoveryGlobalviewExport() { return `${this.baseUrl}/api/in_transit_globalview_export`; },
  // In-Transit Recovery shipment api endpoints
  get InTransitRecoveryShipmentviewFilters() { return `${this.baseUrl}/api/intransit_recovery_shipmentview_filter`; },
  get InTransitRecoveryShipmentviewOverviewTable() { return `${this.baseUrl}/api/intransit_recovery_shipmentview_overview_table`; },
  get InTransitRecoveryShipmentviewExport() { return `${this.baseUrl}/api/export_intransit_recovery_shipmentview_tabledata`; },
  get InTransitRecoveryGlobalFifoGraph() { return `${this.baseUrl}/api/in_transit_globalview_graph`; },
  get InTransitRecoveryShipmentTable() { return `${this.baseUrl}/api/in_transit_globalview_shipmenttable`; },
  // Dispatch priority global api endpoints
  get DispatchPriorityGlobalviewFilters() { return `${this.baseUrl}/api/dispatch_priority_globalview_filter`; },
  get DispatchPriorityGlobalviewOverviewTable() { return `${this.baseUrl}/api/dispatch_priority_globalview_overviewtable`; },
  get DispatchPriorityGlobalviewExport() { return `${this.baseUrl}/api/dispatch_priority_globalview_export`; },
  get DispatchPriorityGlobalFifoGraph() { return `${this.baseUrl}/api/dispatch_prioritization_globalview_fifograph`; },
  get DispatchPriorityShipmentTable() { return `${this.baseUrl}/api/dispatch_prioritization_globalview_shiptable`; },
  // Dispatch priority shipment api endpoints
  get DispatchPriorityShipmentviewFilters() { return `${this.baseUrl}/api/dispatch_priority_shipmentview_filter`; },
  get DispatchPriorityShipmentviewOverviewTable() { return `${this.baseUrl}/api/dispatch_priority_shipmentview_overview_table`; },
  get DispatchPriorityShipmentviewExport() { return `${this.baseUrl}/api/export_dispatch_priority_shipmentview_tabledata`; },

  // RMPM Dashboard api endpoints
  get pvData() { return `${this.baseUrl}/api/pv-data`; },
  get bgData() { return `${this.baseUrl}/api/bg-data`; },
  get demandPageSite() { return `${this.baseUrl}/api/DemandPageSite`; },


  // Smart Source Lens
  get SmartScourceLensFilters() { return `${this.baseUrl}/api/smartsourcelens_filters`; },
  get SmartScourceLensOverviewTable() { return `${this.baseUrl}/api/overview_table_smart_source_lens`; },
  get SmartScourceLensRows() { return `${this.baseUrl}/api/smart_source_lens_rows`; },
  get SmartScourceLensCount() { return `${this.baseUrl}/api/smart_source_lens_count`; },
  get SmartScourceLensTotals() { return `${this.baseUrl}/api/smart_source_lens_totals`; },
  get SmartScourceLensKpi() { return `${this.baseUrl}/api/smart_source_lens_kpi`; },
  get SmartScourceLensPdqAllocationTable() { return `${this.baseUrl}/api/smart_source_lens_allocation_table`; },
  get SmartScourceLensHistoricalGraph() { return `${this.baseUrl}/api/smart_source_lens_historical_graph`; },
  get SmartScourceLensExportData() { return `${this.baseUrl}/api/export_smart_source_lens`; },

    // Dc Supply Insights
  get DcSupplyInsightsFilters() { return `${this.baseUrl}/api/dc_supply_insights_filters`; },
  get DcSupplyInsightsRows() { return `${this.baseUrl}/api/dc_supply_insights_rows`; },
  get DcSupplyInsightsCount() { return `${this.baseUrl}/api/dc_supply_insights_count`; },
  get DcSupplyInsightsTotals() { return `${this.baseUrl}/api/dc_supply_insights_totals`; },
  get DcSupplyInsightsNrMitigationTable() { return `${this.baseUrl}/api/dc_supply_nr_mitigation_table`; },
  get DcSupplyInsightsHistoricalGraph() { return `${this.baseUrl}/api/dc_supply_insights_historical_graph`; },
  get DcSupplyInsightsNrSufficiencyGraph() { return `${this.baseUrl}/api/dc_supply_insights_nr_sufficiency_graph`; },
  get DcSupplyInsightsAllocToPdqGraph() { return `${this.baseUrl}/api/dc_supply_insights_alloc_to_pdq_graph`; },
  get DcSupplyInsightsNrAllocDropGraph() { return `${this.baseUrl}/api/dc_supply_insights_nr_alloc_drop_graph`; },
  get DcSupplyInsightsNrDestNrApoDropGraph() { return `${this.baseUrl}/api/dc_supply_insights_nr_dest_nr_apo_drop_graph`; },
  get DcSupplyInsightsBucketsOverviewGraph() { return `${this.baseUrl}/api/dc_supply_insights_buckets_overview_graph`; },
  get DcSupplyInsightsExportData() { return `${this.baseUrl}/api/export_dc_supply_insights`; },
};

/**
* Initializes the API configuration
*/
const initializeConfig = async () => {
  try {
    const backendUrl = await getBackendUrl();
    apiConfig.baseUrl = backendUrl;
    return apiConfig;
  } catch (error) {
    console.error('Failed to initialize API config:', error);
    // Fallback to production configuration
    apiConfig.baseUrl = "https://samarth-qa-backend.azurewebsites.net";
    return apiConfig;
  }
};

// Start async initialization without top-level await
initializeConfig();

export { initializeConfig };
export default apiConfig;
